import { makeAutoObservable, runInAction } from "mobx";
import {
  CraNodeCoding,
  CreateOrUpdateCraNodeCoding,
} from "../models/CraNodeCoding";
import { CraNodeApi } from "../../cra/services/CraNodeApi";
import {
  getAttributeValue,
  setAttributeByName,
} from "../utils/attribute-value";
import { CraAttributeName } from "@keita/lanepatrol-sdk";
import { CraNodeCodingApi } from "../services/CraNodeCodingApi";
import { RootStore } from "../../shared/stores/RootStore";
import { CraWayApi } from "../../cra/services/CraWayApi";
import { CodingView } from "../models/CodingView";
import { LatestCodingChange } from "../models/LatestCodingChange";
import { SuggestionEngine } from "../utils/attribute-suggestions";
import { nodeReferences } from "../../cra/stores/CraNodeStore";

type craNodeId = string;
type craWayId = string;
const saveDelay = 30 * 1000; //ms

export class CodingStore {
  rootStore;
  suggestionEngine: SuggestionEngine;
  allNodeCodingsByWayId = new Map<craWayId, Map<craNodeId, CraNodeCoding>>();
  dirtyNodeIds = new Set<craNodeId>();

  view = CodingView.defaultValue;
  conflictWarning?: LatestCodingChange;
  savingChanges = false;

  private saveTimer?: NodeJS.Timer;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.suggestionEngine = new SuggestionEngine(rootStore);
    makeAutoObservable(this);
  }

  resetState() {
    runInAction(() => {
      this.allNodeCodingsByWayId = new Map();
      this.savingChanges = false;
      this.stopAutoSaveTimer();
      this.conflictWarning = undefined;
      this.dirtyNodeIds = new Set();
    });
  }

  getFromSelectedWay() {
    const { craWayStore } = this.rootStore;
    if (!craWayStore.selectedId) return undefined;
    return this.allNodeCodingsByWayId.get(craWayStore.selectedId);
  }

  getFromSelectedNode() {
    const { craNodeStore } = this.rootStore;
    if (!craNodeStore.selectedId) return undefined;
    return this.getFromSelectedWay()?.get(craNodeStore.selectedId);
  }

  get changesSaved() {
    return this.dirtyNodeIds.size === 0;
  }

  setView(view: CodingView.Enum) {
    runInAction(() => {
      this.view = view;
    });
  }

  private newMap() {
    runInAction(() => {
      this.allNodeCodingsByWayId = new Map(this.allNodeCodingsByWayId);
    });
  }

  async codeAttribute(
    attributeName: CraAttributeName,
    newValue: string | number | null,
  ) {
    const { craNodeStore } = this.rootStore;
    var selectedNodeIds = craNodeStore.selectedId
      ? [craNodeStore.selectedId]
      : [];

    if (craNodeStore.multiSelectedIds.size) {
      selectedNodeIds = [...craNodeStore.multiSelectedIds];
    }

    selectedNodeIds.forEach((nodeId) => {
      this.setAttributeValue(nodeId, attributeName, newValue);
    });
  }

  setAttributeValue(
    nodeId: string,
    attributeName: CraAttributeName,
    newValue: string | number | null,
  ) {
    const codingEntry = this.getFromSelectedWay()?.get(nodeId);

    if (codingEntry) {
      codingEntry.attributes = setAttributeByName(
        codingEntry.attributes,
        attributeName,
        newValue,
      );

      const suggestions = new Set(codingEntry.suggestions);
      suggestions.delete(attributeName);
      codingEntry.suggestions = [...suggestions];

      this.markAsDirty(nodeId);
      if (this.view !== CodingView.Enum.Spreadsheet) {
        runInAction(() => {
          this.suggestionEngine.makeSuggestions(codingEntry);
        });
      }
      this.newMap();
    }
  }

  async suggestValue(
    nodeCoding: CraNodeCoding,
    attributeName: CraAttributeName,
    newValue: string | number | null,
    overwrite: boolean,
  ) {
    const prevValue = getAttributeValue(attributeName, nodeCoding.attributes);

    if (prevValue !== newValue && (prevValue == null || overwrite)) {
      nodeCoding.attributes = setAttributeByName(
        nodeCoding.attributes,
        attributeName,
        newValue,
      );

      const suggestions = new Set(nodeCoding.suggestions);
      suggestions.add(attributeName);
      nodeCoding.suggestions = [...suggestions];

      this.newMap();
      this.markAsDirty(nodeCoding.craNodeId);
    }
  }

  private setSavingChanges(value: boolean) {
    runInAction(() => {
      this.savingChanges = value;
    });
  }

  async saveChanges() {
    const { craNodeStore } = this.rootStore;
    const batchRequest = this.buildSaveCodingRequests();

    if (!batchRequest.length) return;

    this.setSavingChanges(true);
    await CraNodeCodingApi.batchUpdate(batchRequest)
      .then(({ data }) => {
        data.forEach((savedCoding) => {
          const nodeId = savedCoding.craNodeId;
          const nodeRef = nodeReferences.get(nodeId)!;
          const nodeCoding = this.allNodeCodingsByWayId
            .get(nodeRef.craWayId)!
            .get(nodeId)!;
          nodeCoding.id = savedCoding.id;
        });

        batchRequest.forEach((request) => {
          craNodeStore.refreshNodeScore(request.craNodeId);
        });
      })
      .finally(() => this.setSavingChanges(false));
  }

  async onUnloadSaveChanges() {
    const entryRequests = this.buildSaveCodingRequests();

    if (!entryRequests.length) return;

    await CraNodeCodingApi.batchUpdate(entryRequests);
  }

  private buildSaveCodingRequests() {
    const entryRequests: CreateOrUpdateCraNodeCoding[] = [];

    this.dirtyNodeIds.forEach((nodeId) => {
      const { craWayId } = nodeReferences.get(nodeId)!;

      const codingEntry = this.allNodeCodingsByWayId
        .get(craWayId)!
        .get(nodeId)!;

      this.resetDirty(codingEntry.craNodeId);
      entryRequests.push({
        id: codingEntry.id,
        craNodeId: codingEntry.craNodeId,
        attributes: codingEntry.attributes,
        suggestions: codingEntry.suggestions,
      });
    });

    return entryRequests;
  }

  copyFromPreviousNode() {
    const { craNodeStore } = this.rootStore;
    const nodeCodings = this.getFromSelectedWay();
    const selectedNodeRef = nodeReferences.get(craNodeStore.selectedId!)!;
    const currNodeCoding = nodeCodings?.get(craNodeStore.selectedId!)!;

    if (selectedNodeRef.index > 0) {
      const nodeList = craNodeStore.nodesByWayId.get(selectedNodeRef.craWayId)!;
      const prevNode = nodeList[selectedNodeRef.index - 1];
      const prevNodeCoding = nodeCodings?.get(prevNode.id)!;

      for (const attributeName of Object.values(CraAttributeName)) {
        const value = getAttributeValue(
          attributeName,
          prevNodeCoding.attributes,
        );
        if (value != null) {
          this.suggestValue(currNodeCoding, attributeName, value, false);
        }
      }
    }

    runInAction(() => {
      this.suggestionEngine.makeSuggestions(currNodeCoding);
    });
  }

  startAutoSaveTimer = () => {
    const callback = () => {
      this.saveChanges();
    };
    runInAction(() => {
      this.saveTimer = setInterval(callback, saveDelay);
    });
  };

  stopAutoSaveTimer = () => {
    if (this.saveTimer) {
      runInAction(() => {
        clearInterval(this.saveTimer);
        this.saveTimer = undefined;
      });
    }
  };

  private markAsDirty = (nodeId: string) => {
    runInAction(() => {
      this.dirtyNodeIds.add(nodeId);
    });
  };

  private resetDirty = (nodeId: string) => {
    this.dirtyNodeIds.delete(nodeId);
  };

  clearConflictWarning() {
    runInAction(() => {
      this.conflictWarning = undefined;
    });
  }

  async fetchAttributes(nodeId: string) {
    const response = await CraNodeApi.listCodingEntries(nodeId);
    runInAction(() => {
      this.getFromSelectedWay()?.set(nodeId, response.data);
    });
    this.newMap();
  }

  async fetchAttributesByCraWayId(craWayId: string, cancelIf?: () => boolean) {
    const { data: codingEntries } = await CraWayApi.listCodingEntries(craWayId);
    if (cancelIf?.()) return;

    const wayCodingEntries = new Map();
    codingEntries.forEach((attributes) => {
      wayCodingEntries!.set(attributes.craNodeId, attributes);
    });
    runInAction(() => {
      this.allNodeCodingsByWayId.set(craWayId, wayCodingEntries);
    });
    this.newMap();
  }

  async fetchLatestCodingChange(craWayId: string) {
    const response = await CraWayApi.getLatestCodingChange(craWayId);
    if (response.status === 204 || response.data.me) return;
    runInAction(() => {
      this.conflictWarning = response.data;
    });
  }
}
